import { isPrerender } from '@/utils/helpers';
import { useMediaQuery } from '@material-ui/core';
import { Theme, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { useImageCached } from '../Hooks/useImageCached';
import SEOImage from '../SEOImage';
import { BaseVariants, ImageProps, MappingVariants, TypeVariants } from './Image.interface';
import useStyles from './Image.styles';
import { withVisibility } from '@/HoC/withVisibility';

const mapImageResolutions = {
  xs: 'extraSmall',
  sm: 'small',
  md: 'medium',
  lg: 'large',
  xl: 'extraLarge',
} as { [key: string]: ImageVariantNames };

export const mapImageToSourceSet = ({
  image,
  theme,
  type,
  base,
}: {
  image?: ImageType;
  theme: Theme;
  type: 'background' | 'featured' | 'card' | 'base';
  base: 'small' | 'medium' | 'large' | 'original';
}) => {
  if (type === 'base') {
    const variants = ['extraSmall', 'small', 'medium', 'large', 'extraLarge', 'original']
      .filter((size) => size !== base)
      .slice(0, 3) as ImageVariantNames[];

    const srcSet = variants
      .filter((variant) => !!image?.[variant])
      .map((variant, idx) => `${(image?.[variant] as ImageVariant).url} ${idx + 1}x`)
      .join(', ');

    return srcSet ? <source srcSet={srcSet} /> : <></>;
  }

  if (type === 'featured') {
    const variants = [
      { label: 'featuredMedium', resolution: '1x' },
      { label: 'featuredLarge', resolution: '2x' },
    ] as { label: ImageVariantNames; resolution: string }[];
    const srcSet = variants
      .filter((variant) => !!image?.[variant.label])
      .map((variant) => `${(image?.[variant.label] as ImageVariant).url} ${variant.resolution}`)
      .join(', ');

    return srcSet ? <source srcSet={srcSet} /> : <></>;
  }

  if (type === 'background') {
    const breakpoints: MappingVariants[] = ['xl', 'lg', 'md', 'xs']; // sm
    const bpoints = {
      xs: theme.breakpoints.down('xs').replace('@media ', ''),
      sm: theme.breakpoints.up('sm').replace('@media ', ''),
      md: theme.breakpoints.up('md').replace('@media ', ''),
      lg: theme.breakpoints.up('lg').replace('@media ', ''),
      xl: theme.breakpoints.up('xl').replace('@media ', ''),
    };

    return breakpoints
      .filter((key) => !!(image?.[mapImageResolutions[key]] as ImageVariant)?.url)
      .map((key) => {
        return (
          <source
            key={key}
            data-testid="qa-image-breakpoint"
            media={bpoints[key]}
            srcSet={(image?.[mapImageResolutions[key]] as ImageVariant)?.url}
          />
        );
      });
  }

  if (type === 'card') {
    const variants = [
      { label: 'featuredSmall', resolution: '1x' },
      { label: 'featuredMedium', resolution: '2x' },
      { label: 'featuredLarge', resolution: '3x' },
    ] as { label: ImageVariantNames; resolution: string }[];
    const srcSet = variants
      .filter((variant) => !!image?.[variant.label])
      .map((variant) => `${(image?.[variant.label] as ImageVariant).url} ${variant.resolution}`)
      .join(', ');

    return srcSet ? <source srcSet={srcSet} /> : <></>;
  }

  return <></>;
};

const getBaseImage = ({
  image,
  base,
  type,
}: {
  image: any;
  base: BaseVariants;
  type: TypeVariants;
}) => {
  const baseCap = base && base.charAt(0).toUpperCase() + base.slice(1);

  if (!image || !base) return image;

  if (/original/.test(base)) {
    return image[base] as ImageVariant;
  }

  if (/featured|card/.test(type)) {
    const featuredBase = image[`featured${baseCap}`] || image[`extra${baseCap}`] || image[base];
    return featuredBase as ImageVariant;
  }

  return image[base] as ImageVariant;
};

const Image = ({
  image = {},
  base = 'small',
  size,
  altText: altTextOverride,
  className = '',
  type = 'base',
  roundedCorners = true,
  hasPreloader = true,
  position,
}: ImageProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prerender = React.useRef(isPrerender());
  const [showLoader, setShowLoader] = useState(!prerender.current);
  const [visible, setVisible] = useState(prerender.current);
  const [docloaded, setDocloaded] = useState(document.readyState === 'complete');
  const { fullWidthImage = true } = image || {};
  const isFullWidth = useMemo(
    () => fullWidthImage || /background/.test(type),
    [fullWidthImage, type],
  );
  const classes = useStyles({});
  const originalImage = useMemo(() => image && (image.original as ImageVariant), [image]);
  const match = useMemo(
    () =>
      (originalImage && /[.](jpg|svg|jpeg|png)\/{0,}/.exec(originalImage.url)) || ['', 'non-svg'],
    [originalImage],
  );
  const extension = useMemo(() => match[1], [match]);
  const baseImage = useMemo(() => getBaseImage({ image, base, type }), [image, base, type]);
  const [isImageCached] = useImageCached({
    imageSrc: baseImage?.url,
    isLoading: showLoader,
    isMobile,
  });
  const { altText } = image || {};
  const isSVG = /^svg/.test(extension);
  const sourceSet = useMemo(
    () =>
      image &&
      visible &&
      !/original/.test(base) &&
      !isSVG &&
      mapImageToSourceSet({ image, theme, type, base }),
    [image, visible, base, isSVG, theme, type],
  );
  const isBackground = useMemo(
    () => (size && !isSVG) || type === 'background',
    [isSVG, size, type],
  );
  const hasDimensions = useMemo(() => Boolean(baseImage?.width && baseImage?.height), [baseImage]);
  const imageHeight = useMemo(
    () => (hasDimensions || !baseImage?.height ? 'auto' : baseImage?.height),
    [baseImage, hasDimensions],
  );
  const imageWidth = useMemo(
    () => (hasDimensions || !baseImage?.width ? 'auto' : baseImage?.width),
    [baseImage, hasDimensions],
  );

  const pictureClasses = clsx(
    classes.image,
    className,
    'gntc-image',
    isFullWidth && 'is-full-width',
    size && size === 'cover' && 'is-cover',
    size && size === 'contain' && 'is-contain',
    roundedCorners && 'is-rounded-corners',
    isBackground && 'is-background',
    hasDimensions && 'has-dimensions',
  );

  const preloaderClasses = clsx(classes.preloader, showLoader && hasPreloader ? 'is-loading' : '');
  const preloaderStyles = useMemo(
    () => ({
      background: image?.preloader?.url && `url('${image?.preloader?.url}') repeat center center`,
    }),
    [image?.preloader?.url],
  );

  const showPreloader = useMemo(
    () => hasPreloader && !isSVG && !prerender.current && !isImageCached,
    [hasPreloader, isSVG, isImageCached],
  );

  const handleVisibilityChange = useCallback((isVisible: boolean) => {
    if (isVisible && !visible) {
      setVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisible(prerender.current);
    setShowLoader(!prerender.current);
  }, [image.original?.url]);

  useEffect(() => {
    if (document.readyState === 'complete') return () => undefined;

    const loadReady = () => setDocloaded(true);
    window.addEventListener('load', loadReady);
    return () => window.removeEventListener('load', loadReady);
  }, []);

  if (!baseImage) {
    return null;
  }

  return (
    <>
      <VisibilitySensor
        active={!visible}
        onChange={handleVisibilityChange}
        delayedCall
        partialVisibility
        scrollCheck
      >
        <picture className={pictureClasses} data-testid="qa-image">
          {sourceSet}
          {showPreloader && <div className={preloaderClasses} style={preloaderStyles} />}
          {(visible || docloaded) && (
            <img
              style={{ objectPosition: position }}
              onLoad={() => setShowLoader(false)}
              src={baseImage.url}
              height={imageHeight}
              width={imageWidth}
              alt={altTextOverride || altText || ''}
              className={clsx(!showLoader && 'loaded')}
            />
          )}
          {!isBackground && hasDimensions && (
            <svg
              className={classes.svgArea}
              focusable="false"
              viewBox={`0 0 ${baseImage?.width} ${baseImage?.height}`}
              aria-hidden="true"
            >
              <rect x="0" y="0" width={baseImage?.width} height={baseImage?.height} />
            </svg>
          )}
        </picture>
      </VisibilitySensor>
      {!isEmpty(image) && <SEOImage image={image} />}
    </>
  );
};

export default memo(withVisibility<ImageProps>(Image, ['image', 'visibility']));
